import React, { useEffect, useState } from "react";
import { Flame, Barbell, Trophy, Medal, Lock, Sparkle } from "@phosphor-icons/react";
import api from "@/lib/api";

// Motivational "momentum" strip — a live coaching streak + milestone badges.
// Everything here is read-only and gates nothing; it exists to give coaches a
// reason to come back and a sense of progress. Self-hides on error.
export default function CoachMomentum() {
  const [m, setM] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    api.get("/coach/momentum")
      .then(({ data }) => setM(data))
      .catch(() => setFailed(true));
  }, []);

  if (failed) return null;
  if (!m) {
    return (
      <div className="tactical-card p-5 mb-8 animate-pulse" data-testid="coach-momentum-loading">
        <div className="h-5 w-40 bg-white/10 rounded mb-4" />
        <div className="h-16 w-full bg-white/5 rounded" />
      </div>
    );
  }

  const streak = m.streak_days || 0;
  const streakCopy = streak === 0
    ? "Log a client workout to start a streak"
    : streak === 1
    ? "1 day of roster activity — keep it rolling"
    : `${streak} days of non-stop roster activity`;

  const stats = [
    { icon: Barbell, label: "WORKOUTS · 30D", val: m.workouts_30d ?? 0, tint: "kpi-blue" },
    { icon: Trophy, label: "PRS COACHED", val: m.prs_coached ?? 0, tint: "kpi-amber" },
    { icon: Medal, label: "BADGES EARNED", val: `${m.earned_count ?? 0}/${(m.badges || []).length}`, tint: "kpi-violet" },
  ];

  const next = m.next_badge;

  return (
    <div className="tactical-card p-5 mb-8" data-testid="coach-momentum">
      <div className="flex items-center gap-2 mb-4">
        <Sparkle size={14} weight="fill" className="text-heat" />
        <span className="label-tiny text-brand-accent">// YOUR MOMENTUM</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-5">
        {/* Streak hero */}
        <div
          className={`flex items-center gap-3 border p-4 ${
            streak > 0 ? "border-heat/40 bg-heat/[0.05]" : "border-slate-200"
          }`}
          data-testid="momentum-streak"
        >
          <Flame
            size={38}
            weight="fill"
            className={streak > 0 ? "text-heat" : "text-text-secondary/40"}
          />
          <div>
            <div className="font-heading text-3xl tabular-nums leading-none">
              {streak}<span className="text-sm text-text-secondary ml-1">day{streak === 1 ? "" : "s"}</span>
            </div>
            <div className="text-[11px] text-text-secondary mt-1 max-w-[180px] leading-snug">
              {streakCopy}
            </div>
          </div>
        </div>

        {/* Stat tiles */}
        {stats.map((s) => (
          <div key={s.label} className={`kpi-tile ${s.tint} flex flex-col justify-center`} data-testid={`momentum-stat-${s.label}`}>
            <div className="flex items-center gap-1.5 mb-2">
              <s.icon size={14} weight="duotone" style={{ color: "var(--kpi)" }} />
              <span className="label-tiny text-[10px]">{s.label}</span>
            </div>
            <div className="kpi-num font-heading text-2xl tabular-nums">{s.val}</div>
          </div>
        ))}
      </div>

      {/* Next badge nudge */}
      {next && (
        <div className="border border-slate-200 p-3 mb-4 flex items-center gap-3" data-testid="momentum-next-badge">
          <Lock size={16} weight="duotone" className="text-text-secondary shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-xs">
              Next badge: <span className="font-heading text-brand-accent">{next.label}</span>
            </div>
            <div className="h-1.5 bg-white/10 overflow-hidden rounded-full mt-1.5">
              <div
                className="h-full bg-brand-accent transition-all rounded-full"
                style={{ width: `${Math.max(4, Math.round((next.progress / next.threshold) * 100))}%` }}
              />
            </div>
          </div>
          <span className="label-tiny text-[10px] text-text-secondary tabular-nums shrink-0">
            {next.progress}/{next.threshold}
          </span>
        </div>
      )}

      {/* Badge rail */}
      <div className="flex gap-2 overflow-x-auto pb-1" data-testid="momentum-badges">
        {(m.badges || []).map((b) => (
          <div
            key={b.key}
            title={b.earned ? `${b.label} — earned` : `${b.label} — ${b.progress}/${b.threshold}`}
            className={`shrink-0 flex items-center gap-2 border px-3 py-2 transition-colors ${
              b.earned
                ? "border-heat/40 bg-heat/[0.06] text-white"
                : "border-slate-200 text-text-secondary/60"
            }`}
            data-testid={`momentum-badge-${b.key}`}
          >
            <Medal
              size={16}
              weight={b.earned ? "fill" : "duotone"}
              className={b.earned ? "text-heat" : "text-text-secondary/40"}
            />
            <span className="label-tiny text-[10px] whitespace-nowrap">{b.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
